/**
 * 牌效评分模型（a1）
 *
 * 相比 v1 的「(8-向听)×1000 + 进张数」，本模块实现工业标准的综合评分：
 *
 *   ① 向听数         权重最高（决定基本进度）
 *   ② 有效进张数      精确计算（区分好形/愚形）
 *   ③ 好形率         两面搭子 > 坎张/边张
 *   ④ 手役价值       断幺/役牌/平和/三色/一气 等可能性
 *   ⑤ 宝牌价值       宝牌及宝牌周边加权
 *   ⑥ 孤立牌价值     中张 > 边张，字牌最低（但役牌例外）
 *   ⑦ 安全度         危険牌扣分（防守接口，a2 完整实现）
 *
 * 参考：天鳳/雀魂的「牌理」体系（进张数 + 好形率 + 手役 + 宝牌）
 */

import {
  COPIES_PER_TILE,
  Counts,
  indexToName,
  isHonor,
  isSimple,
  isTerminal,
  isYaochu,
  tileIndex,
  type Tile,
} from '../tile.js';
import { shanten } from '../shanten.js';
import { analyzeWaits } from '../wait.js';
import { analyzeFuriten, type FuritenTracker } from './furiten.js';
import type { GameState, PlayerState, Seat } from './state.js';
import { handToCounts, remainingCount } from './rules.js';

// ════════════════════════════════════════════════════════════════
// 配置
// ════════════════════════════════════════════════════════════════

/** 牌效权重配置 */
export interface EfficiencyWeights {
  /** 向听数权重（每减少一向听 = 加分） */
  readonly shanten: number;
  /** 有效进张权重 */
  readonly ukeire: number;
  /** 好形进张权重（额外加分） */
  readonly goodShape: number;
  /** 手役价值权重 */
  readonly yaku: number;
  /** 宝牌权重 */
  readonly dora: number;
  /** 孤立牌基础价值权重 */
  readonly isolation: number;
  /** 安全度权重（负分 = 危険） */
  readonly safety: number;
}

export const DEFAULT_WEIGHTS: EfficiencyWeights = {
  shanten: 10000,
  ukeire: 100,
  goodShape: 50,
  yaku: 30,
  dora: 40,
  isolation: 1,
  safety: 0, // a2 启用
};

// ════════════════════════════════════════════════════════════════
// 核心：评估一个打牌候选
// ════════════════════════════════════════════════════════════════

/** 单个打牌候选的评估结果 */
export interface DiscardEvaluation {
  /** 打出的牌 */
  readonly tile: Tile;
  /** 综合得分（越高越好） */
  readonly score: number;
  /** 打牌后的向听数 */
  readonly shantenAfter: number;
  /** 有效进张总数（含重复） */
  readonly ukeire: number;
  /** 好形进张数 */
  readonly goodUkeire: number;
  /** 手役价值分 */
  readonly yakuValue: number;
  /** 宝牌相关分 */
  readonly doraValue: number;
  /** 各分项明细（调试用） */
  readonly breakdown: Record<string, number>;
}

/**
 * 评估「打掉某张牌」的好坏
 */
export function evaluateDiscard(
  state: GameState,
  seat: Seat,
  tile: Tile,
  weights: EfficiencyWeights = DEFAULT_WEIGHTS,
  tracker?: FuritenTracker,
): DiscardEvaluation {
  const player = state.players[seat];
  const after = player.hand.filter((t) => t.id !== tile.id);
  const counts = handToCounts(after);
  const meldCount = player.melds.length;

  const s = shanten(counts, meldCount);

  // ── ②③ 有效进张 / 和牌张 ──
  // ⚠️ 0 向听（已听牌）时，语义应从"进张数"切换为"和牌张数"：
  //    听牌状态下摸到和牌牌会算成"向听数下降"，导致多面愚形听反而分高。
  //    正确做法：听牌时评估"和牌张数"（等待的牌 × 剩余枚数）。
  const { ukeire, goodUkeire } = s <= 0
    ? analyzeWaitCount(state, seat, counts, meldCount)
    : analyzeUkeire(state, seat, counts, meldCount, s);

  // ── ④ 手役价值 ──
  const yakuValue = evaluateYakuPotential(counts, player, state);

  // ── ⑤ 宝牌价值 ──
  const doraValue = evaluateDoraValue(counts, player, state, tile);

  // ── ⑥ 孤立牌价值 ──
  // ⚠️ 语义：evaluateIsolationValue 返回「该牌的保留价值」。
  //    打牌时，打掉价值高的牌 = 坏决策 → 必须**扣分**（取负）。
  //    （我犯过：直接加分，导致 AI 专挑中张好牌打掉、留着字牌孤张）
  const keepValue = evaluateIsolationValue(tile, state, seat);
  const isolationValue = -keepValue;

  // ── ⑦ 安全度（a2 完整实现）──
  const safetyValue = evaluateSafety(state, seat, tile, tracker);

  // ── 综合 ──
  const breakdown: Record<string, number> = {
    shanten: (8 - s) * weights.shanten,
    ukeire: ukeire * weights.ukeire,
    goodShape: goodUkeire * weights.goodShape,
    yaku: yakuValue * weights.yaku,
    dora: doraValue * weights.dora,
    isolation: isolationValue * weights.isolation,
    safety: safetyValue * weights.safety,
  };

  const score = Object.values(breakdown).reduce((a, b) => a + b, 0);

  return {
    tile,
    score,
    shantenAfter: s,
    ukeire,
    goodUkeire,
    yakuValue,
    doraValue,
    breakdown,
  };
}

// ════════════════════════════════════════════════════════════════
// 有效进张的精确分析
// ════════════════════════════════════════════════════════════════

/**
 * 分析有效进张
 *
 * 区分「好形进张」和「愚形进张」：
 *   · 摸牌后能形成**两面搭子**或**直接完成面子** → 好形
 *   · 摸牌后形成坎张/边张 → 愚形
 *
 * @returns ukeire = 总进张数（含重复牌数），goodUkeire = 好形进张数
 */
export function analyzeUkeire(
  state: GameState,
  seat: Seat,
  counts: Counts,
  meldCount: number,
  currentShanten: number,
): { ukeire: number; goodUkeire: number } {
  if (counts.length !== 34) return { ukeire: 0, goodUkeire: 0 };

  let ukeire = 0;
  let goodUkeire = 0;

  // 手牌总张数 = 手牌 + 副露折算（13 = 未摸牌 / 14 = 摸牌后）
  const handSize = counts.reduce((a, b) => a + b, 0) + meldCount * 3;
  if (handSize > 14 || handSize < 13) return { ukeire: 0, goodUkeire: 0 };

  // 已经是和牌形 → 无进张
  if (currentShanten <= -1) return { ukeire: 0, goodUkeire: 0 };

  for (let i = 0; i < 34; i++) {
    if (counts[i] >= COPIES_PER_TILE) continue; // 4 张已满，无剩余

    const test = [...counts];
    test[i]++;
    const newShanten = shanten(test, meldCount);
    if (newShanten >= currentShanten) continue; // 不是进张

    const available = remainingCount(state, seat, i);
    if (available <= 0) continue;

    ukeire += available;

    // ── 判断是否好形 ──
    if (isGoodShape(test, i, meldCount, newShanten)) {
      goodUkeire += available;
    }
  }

  return { ukeire, goodUkeire };
}

/**
 * 听牌时的「和牌张」分析（0 向听专用）
 *
 * 与 analyzeUkeire 的区别：
 *   · analyzeUkeire：13 张 → 摸牌 → 向听数下降的牌（进张）
 *   · analyzeWaitCount：13 张已听牌 → 哪些牌能和（和牌张）
 *
 * 同时评估听牌形质量（好形 = 两面听，愚形 = 坎张/边张/单骑）
 */
export function analyzeWaitCount(
  state: GameState,
  seat: Seat,
  counts: Counts,
  meldCount: number,
): { ukeire: number; goodUkeire: number } {
  const handSize = counts.reduce((a, b) => a + b, 0) + meldCount * 3;
  if (handSize !== 13) return { ukeire: 0, goodUkeire: 0 };

  let total = 0;
  let good = 0;

  for (let i = 0; i < 34; i++) {
    if (counts[i] >= COPIES_PER_TILE) continue;
    const test = [...counts];
    test[i]++;
    if (shanten(test, meldCount) !== -1) continue; // 不是和牌张

    const available = remainingCount(state, seat, i);
    if (available <= 0) continue;
    total += available;

    // 好形判定：和牌张形成两面听（两侧都能和）→ 近似用"该牌是两面搭子的完成张"
    if (isGoodWait(counts, i)) good += available;
  }

  return { ukeire: total, goodUkeire: good };
}

/**
 * 判断某个和牌张是否属于「好形听」（两面听）
 *
 * 两面听的完成张：与手中相邻两张组成顺子，且两端都成立
 *   例：手中有 23s，听 1s/4s → 1s 和 4s 都是好形
 *       手中有 13s，听 2s → 坎张（愚形）
 *       手中有 12s，听 3s → 边张（愚形）
 */
function isGoodWait(counts: Counts, winTile: number): boolean {
  if (isHonor(winTile)) {
    // 字牌：对倒听（有对子）→ 算好形
    return counts[winTile] >= 2;
  }

  const suitStart = Math.floor(winTile / 9) * 9;
  const pos = winTile - suitStart;
  const c = [...counts];

  // 检查是否能和左右两侧的牌组成顺子（两面）
  // 例如 winTile=1s(idx 18)，手中有 2s 3s → 两面听（1s/4s 都是）
  let leftNeighbor = false;
  let rightNeighbor = false;
  if (pos + 2 <= 8 && c[winTile + 1] > 0 && c[winTile + 2] > 0) leftNeighbor = true; // 1s 配 2s3s
  if (pos - 2 >= 0 && c[winTile - 1] > 0 && c[winTile - 2] > 0) rightNeighbor = true; // 3s 配 1s2s

  return leftNeighbor || rightNeighbor;
}

/**
 * 判断摸到某张牌后是否为「好形」
 *
 * 好形定义：摸牌后，在最优分解中存在**两面搭子**或**已完成面子**
 * （因为两面搭子进张数多，价值高于坎张/边张）
 */
function isGoodShape(counts: Counts, drawn: number, meldCount: number, newShanten: number): boolean {
  // 简化判定：摸到的牌是否与手牌形成"两侧连续"
  // 例：手中有 3m4m，摸 2m/5m → 形成 234m/345m（好形顺子）
  //     手中有 3m5m，摸 4m → 坎张（愚形）
  if (isHonor(drawn)) {
    // 字牌：只有成刻子才算好形（役牌价值另算）
    const c = [...counts];
    return c[drawn] >= 3;
  }

  const suitStart = Math.floor(drawn / 9) * 9;
  const pos = drawn - suitStart;
  const c = [...counts];

  // 检查左右两侧是否能形成顺子
  let good = false;
  // 右侧：drawn, drawn+1, drawn+2
  if (pos + 2 <= 8 && c[drawn + 1] > 0 && c[drawn + 2] > 0) good = true;
  // 中间：drawn-1, drawn, drawn+1
  if (pos - 1 >= 0 && pos + 1 <= 8 && c[drawn - 1] > 0 && c[drawn + 1] > 0) good = true;
  // 左侧：drawn-2, drawn-1, drawn
  if (pos - 2 >= 0 && c[drawn - 2] > 0 && c[drawn - 1] > 0) good = true;
  // 对子/刻子
  if (c[drawn] >= 2) good = true;

  return good;
}

// ════════════════════════════════════════════════════════════════
// 手役价值
// ════════════════════════════════════════════════════════════════

/**
 * 评估手牌的手役潜力
 *
 * 检查当前手牌"朝着哪个役种发展"：
 *   · 断幺九（无幺九牌）→ 容易做，高分
 *   · 役牌（三元牌/场风/自风 成对/成刻）→ 确定役
 *   · 平和（全顺子 + 无役牌雀头）→ 门清好形
 *   · 三色/一气/混一色 等 → 顺子连贯性
 */
function evaluateYakuPotential(counts: Counts, player: PlayerState, state: GameState): number {
  let value = 0;

  // ── 断幺九：手中无幺九牌 ──
  let hasYaochu = false;
  for (let i = 0; i < 34; i++) {
    if (counts[i] > 0 && isYaochu(i)) {
      hasYaochu = true;
      break;
    }
  }
  if (!hasYaochu) value += 3;
  else {
    // 幺九牌越少越好
    let yaochuCount = 0;
    for (let i = 0; i < 34; i++) if (counts[i] > 0 && isYaochu(i)) yaochuCount++;
    value += Math.max(0, 3 - yaochuCount);
  }

  // ── 役牌：三元牌 / 场风 / 自风 ──
  const roundWindIdx = ['east', 'south', 'west', 'north'].indexOf(state.round.roundWind);
  const seatWind = seatToWindIndex(player.seat, state.round.roundWind);
  for (let i = 0; i < 34; i++) {
    const c = counts[i];
    if (c === 0) continue;
    const isYakuhai =
      (i >= 31 && i <= 33) || // 白发中
      i === 27 + roundWindIdx || // 场风
      i === 27 + seatWind; // 自风
    if (isYakuhai) {
      if (c >= 2) value += 4; // 已有对子 → 很有价值
      else if (c === 1) value += 1;
    }
  }

  // ── 平和可能性：顺子多 + 无役牌雀头 ──
  // 简化：统计搭子/顺子的"顺子倾向"
  if (player.melds.length === 0) value += 1; // 门清加分

  // ── 混一色/清一色潜力：某花色占比高 ──
  const suitCounts = [0, 0, 0, 0];
  for (let i = 0; i < 34; i++) {
    suitCounts[Math.floor(i / 9)] += counts[i];
  }
  const total = counts.reduce((a, b) => a + b, 0);
  for (let s = 0; s < 4; s++) {
    const ratio = total > 0 ? suitCounts[s] / total : 0;
    if (ratio >= 0.8) value += 5; // 清一色潜力
    else if (ratio >= 0.6) value += 2; // 混一色潜力
  }

  return value;
}

/** 座位 → 风索引（0=东 1=南 2=西 3=北）*/
function seatToWindIndex(seat: Seat, roundWind: string): number {
  const winds = ['east', 'south', 'west', 'north'];
  const roundOffset = winds.indexOf(roundWind);
  return (seat + roundOffset) % 4;
}

// ════════════════════════════════════════════════════════════════
// 宝牌价值
// ════════════════════════════════════════════════════════════════

/** 宝牌循环：1→2→...→9→1；东南西北→东；白发中→白 */
export function nextDoraIndex(indicator: number): number {
  if (indicator < 9) {
    // 万子 1-9
    return indicator === 8 ? 0 : indicator + 1;
  }
  if (indicator < 18) {
    const local = indicator - 9;
    return 9 + (local === 8 ? 0 : local + 1);
  }
  if (indicator < 27) {
    const local = indicator - 18;
    return 18 + (local === 8 ? 0 : local + 1);
  }
  // 字牌
  if (indicator <= 30) {
    // 风牌 27-30（东南西北）→ 循环
    const local = indicator - 27;
    return 27 + (local === 3 ? 0 : local + 1);
  }
  // 三元牌 31-33（白发中）→ 循环
  const local = indicator - 31;
  return 31 + (local === 2 ? 0 : local + 1);
}

/** 当前宝牌（含指示牌的下一张） */
export function currentDora(indicator: number): number {
  return nextDoraIndex(indicator);
}

/**
 * 评估手牌的宝牌价值
 */
function evaluateDoraValue(
  counts: Counts,
  player: PlayerState,
  state: GameState,
  discarded?: Tile,
): number {
  let value = 0;

  const indicators = state.wall.doraIndicators.map(tileIndex);
  const doraTiles = new Set(indicators.map(nextDoraIndex));

  // 手中的宝牌
  for (const d of doraTiles) {
    value += counts[d] * 2;
  }

  // 赤宝牌
  for (let i = 0; i < 34; i++) {
    if (counts[i] > 0 && [0, 9, 18].includes(i)) {
      // 1m/1p/1s 可能是赤牌（要看具体 Tile 的 red 标记，这里简化）
    }
  }
  for (const t of player.hand) {
    if (t.red) value += 4;
  }

  return value;
}

// ════════════════════════════════════════════════════════════════
// 孤立牌价值
// ════════════════════════════════════════════════════════════════

/**
 * 评估一张牌的「孤立价值」
 *
 * 打牌时要优先打掉**价值低**的牌：
 *   · 字牌（非役牌）价值最低
 *   · 1/9 价值低（只能形成边张）
 *   · 2/8 稍高
 *   · 3-7 中张价值最高（可形成多种顺子）
 *
 * @returns 价值分（越高 = 越应该保留）
 */
export function evaluateIsolationValue(tile: Tile, state: GameState, seat: Seat): number {
  const idx = tileIndex(tile);

  if (isHonor(idx)) {
    // 役牌价值高，客风（无关风）最低
    const roundWindIdx = ['east', 'south', 'west', 'north'].indexOf(state.round.roundWind);
    const sw = seatToWindIndex(seat, state.round.roundWind);
    if (idx >= 31 && idx <= 33) return 3; // 三元牌
    if (idx === 27 + roundWindIdx || idx === 27 + sw) return 3; // 场风/自风
    return 0; // 客风最低
  }

  const pos = idx % 9; // 0-8
  // 中张价值高：距离两端越远越高
  const centerDist = Math.min(pos, 8 - pos); // 0(边) 到 4(中)
  return 1 + centerDist * 0.5;
}

// ════════════════════════════════════════════════════════════════
// 安全度（a2 接口）
// ════════════════════════════════════════════════════════════════

/**
 * 评估一张牌的安全度（a1 先给基础实现，a2 深化）
 *
 * 返回 0-100，越高越安全：
 *   · 现物（对手打过的牌）= 100
 *   · 立直后自家打过的 = 100
 *   · 其他 = 根据危险度估计
 */
function evaluateSafety(
  state: GameState,
  seat: Seat,
  tile: Tile,
  tracker?: FuritenTracker,
): number {
  const idx = tileIndex(tile);

  // 现物：所有对手的舍牌中都没有这张 → 不完全安全，但降低风险
  let dangerousFor = 0;
  for (const p of state.players) {
    if (p.seat === seat) continue;
    // 对手立直 → 其舍牌是现物
    if (p.riichi && p.discards.some((t) => tileIndex(t) === idx)) {
      dangerousFor = 0; // 对这家绝对安全
    } else if (p.riichi) {
      dangerousFor++;
    }
  }

  return dangerousFor === 0 ? 100 : Math.max(0, 100 - dangerousFor * 30);
}

// ════════════════════════════════════════════════════════════════
// 对外接口
// ════════════════════════════════════════════════════════════════

/**
 * 选择最优打牌（牌效版）
 */
export function chooseBestDiscardV2(
  state: GameState,
  seat: Seat,
  weights: EfficiencyWeights = DEFAULT_WEIGHTS,
  tracker?: FuritenTracker,
): DiscardEvaluation {
  const player = state.players[seat];
  const seen = new Set<number>();
  let best: DiscardEvaluation | null = null;

  for (const tile of player.hand) {
    const idx = tileIndex(tile);
    if (seen.has(idx)) continue;
    seen.add(idx);

    const ev = evaluateDiscard(state, seat, tile, weights, tracker);
    if (!best || ev.score > best.score) best = ev;
  }

  if (!best) throw new Error('无可打之牌');
  return best;
}

/**
 * 列出所有打牌候选并排序（用于调试 / UI）
 */
export function rankDiscards(
  state: GameState,
  seat: Seat,
  weights: EfficiencyWeights = DEFAULT_WEIGHTS,
  tracker?: FuritenTracker,
): DiscardEvaluation[] {
  const player = state.players[seat];
  const seen = new Set<number>();
  const results: DiscardEvaluation[] = [];

  for (const tile of player.hand) {
    const idx = tileIndex(tile);
    if (seen.has(idx)) continue;
    seen.add(idx);
    results.push(evaluateDiscard(state, seat, tile, weights, tracker));
  }

  return results.sort((a, b) => b.score - a.score);
}

/** 格式化评估结果（调试/教学用） */
export function formatEvaluation(ev: DiscardEvaluation): string {
  const parts = Object.entries(ev.breakdown)
    .filter(([, v]) => v !== 0)
    .map(([k, v]) => `${k}=${v.toFixed(0)}`)
    .join(' ');
  return `打 ${indexToName(tileIndex(ev.tile))} → ${ev.shantenAfter}向听 进张${ev.ukeire}(好形${ev.goodUkeire}) | ${parts} | 总${ev.score.toFixed(0)}`;
}
